import styled from '@emotion/styled';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { tokens, Tabs } from '@hotjar/hotsauce';
import type { Site } from '@hotjar/site';
import { TrackingCodeTab } from './TrackingCodeTab';
import { TrackingCodeVerificationTab } from './TrackingCodeVerificationTab';

export enum TrackingCodeModalTabs {
  snippet,
  verification,
}

const StyledTabs = styled(Tabs)`
  margin-top: ${tokens.spacer.x2};
`;

const SiteId = styled.span`
  ${tokens.text('body2')};
  float: right;
  height: ${tokens.spacer.x6};
  line-height: ${tokens.spacer.x6};
  margin-right: ${tokens.spacer.x3};

  b {
    font-weight: 600;
  }
`;

export interface TrackingCodeTabsProps {
  site: Site;
  activeTab?: 'install' | 'verify';
  onSuccessClick: () => void;
}

export const TrackingCodeTabs = ({
  site,
  activeTab = 'install',
  onSuccessClick,
}: TrackingCodeTabsProps): JSX.Element => {
  const [selectedTab, setSelectedTab] = useState(
    activeTab === 'install' ? TrackingCodeModalTabs.snippet : TrackingCodeModalTabs.verification,
  );
  const isSnippetTabSelected = selectedTab === TrackingCodeModalTabs.snippet;
  const isVerificationTabSelected = !isSnippetTabSelected;
  const { t } = useTranslation();

  return (
    <StyledTabs>
      <Tabs.Nav>
        <Tabs.Tab
          isSelected={isSnippetTabSelected}
          onClick={() => setSelectedTab(TrackingCodeModalTabs.snippet)}
        >
          {t('activation:tracking-code.snippet-tab-title')}
        </Tabs.Tab>
        <Tabs.Tab
          isSelected={isVerificationTabSelected}
          onClick={() => setSelectedTab(TrackingCodeModalTabs.verification)}
        >
          {t('activation:tracking-code.verification-tab-title')}
        </Tabs.Tab>
        <SiteId>
          <Trans i18nKey="activation:tracking-code.site-id" values={{ siteId: site.id }}>
            Site ID:
            <b>{site.id}</b>
          </Trans>
        </SiteId>
      </Tabs.Nav>
      <Tabs.Panel isVisible={isSnippetTabSelected}>
        <TrackingCodeTab
          site={site}
          onVerifyClick={() => setSelectedTab(TrackingCodeModalTabs.verification)}
        />
      </Tabs.Panel>
      <Tabs.Panel isVisible={isVerificationTabSelected}>
        {site ? <TrackingCodeVerificationTab site={site} onSuccessClick={onSuccessClick} /> : null}
      </Tabs.Panel>
    </StyledTabs>
  );
};
